import React, { useEffect, useState } from "react";
import { fetchTills, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// TillsOverviewPage (manager-facing) — every cashier's till for a business day
// as one table: cashier, point, opening float, sales, counted vs expected, the
// cash variance (red when non-zero) and status. A date filter scopes the day.
// Reuses GET /tills (a manager sees all tills). Same trilingual `S` +
// formatPrice conventions as the other reporting pages.
const S = {
  en: {
    title: "Tills",
    subtitle: "Every cashier's drawer for the day",
    date: "Business day",
    salesperson: "Cashier", all_salespeople: "All cashiers",
    department: "Department", all_departments: "All departments",
    cashier: "Cashier", point: "Point", opening: "Opening float",
    sales: "Sales", counted: "Counted", expected: "Expected",
    variance: "Variance", status: "Status",
    open: "Open", closed: "Closed",
    none: "No tills for this day.",
    loading: "Loading…",
    err: "Could not load tills.",
    open_count: "open", closed_count: "closed",
  },
  am: {
    // TODO verify
    title: "የገንዘብ ሳጥኖች",
    subtitle: "Every cashier's drawer for the day",
    date: "Business day",
    salesperson: "ገንዘብ ተቀባይ", all_salespeople: "ሁሉም ገንዘብ ተቀባዮች",
    department: "ክፍል", all_departments: "ሁሉም ክፍሎች",
    cashier: "Cashier", point: "Point", opening: "Opening float",
    sales: "Sales", counted: "Counted", expected: "Expected",
    variance: "Variance", status: "Status",
    open: "Open", closed: "Closed",
    none: "No tills for this day.",
    loading: "Loading…",
    err: "Could not load tills.",
    open_count: "open", closed_count: "closed",
  },
  om: {
    // TODO verify
    title: "Kaassaawwan",
    subtitle: "Every cashier's drawer for the day",
    date: "Business day",
    salesperson: "Kaasherii", all_salespeople: "Kaasherota hunda",
    department: "Kutaa", all_departments: "Kutaalee hunda",
    cashier: "Cashier", point: "Point", opening: "Opening float",
    sales: "Sales", counted: "Counted", expected: "Expected",
    variance: "Variance", status: "Status",
    open: "Open", closed: "Closed",
    none: "No tills for this day.",
    loading: "Loading…",
    err: "Could not load tills.",
    open_count: "open", closed_count: "closed",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const TH = { textAlign: "left", color: "#5b6472", padding: "5px 8px", fontSize: 12.5 };
const TR = { textAlign: "right" };
const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };
const iso0 = () => new Date().toISOString().slice(0, 10);

export default function TillsOverviewPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);

  const [businessDate, setBusinessDate] = useState(iso0());
  const [salesperson, setSalesperson] = useState("");   // "" = all; else cashier_staff_id
  const [department, setDepartment] = useState("");     // "" = all; else point
  const [tills, setTills] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    setErr("");
    fetchTills({ businessDate })
      .then((d) => setTills(d && Array.isArray(d.tills) ? d.tills : []))
      .catch(() => { setErr(t("err")); setTills([]); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessDate]);

  // Distinct salespeople present in the loaded day (for the filter dropdown).
  const people = Object.values((tills || []).reduce((m, x) => {
    if (x.cashier_staff_id != null && !m[x.cashier_staff_id]) {
      m[x.cashier_staff_id] = { id: x.cashier_staff_id, name: x.cashier_name || `#${x.cashier_staff_id}` };
    }
    return m;
  }, {})).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  // Distinct departments (till points) present in the loaded day.
  const depts = Array.from(new Set((tills || []).map((x) => x.point).filter(Boolean))).sort();
  const shown = (tills || []).filter((x) =>
    (!salesperson || String(x.cashier_staff_id) === String(salesperson)) &&
    (!department || x.point === department));

  const openCount = shown.filter((x) => x.status === "open").length;
  const closedCount = shown.filter((x) => x.status === "closed").length;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("date")}{" "}
          <input type="date" style={{ ...inp, marginLeft: 4 }} value={businessDate}
            onChange={(e) => setBusinessDate(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("salesperson")}{" "}
          <select style={{ ...inp, marginLeft: 4 }} value={salesperson}
            onChange={(e) => setSalesperson(e.target.value)}>
            <option value="">{t("all_salespeople")}</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("department")}{" "}
          <select style={{ ...inp, marginLeft: 4 }} value={department}
            onChange={(e) => setDepartment(e.target.value)}>
            <option value="">{t("all_departments")}</option>
            {depts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        {tills && (
          <span style={{ fontSize: 12.5, color: "#5f6b78" }}>
            <b style={{ color: "#0F6E56" }}>{openCount}</b> {t("open_count")} ·{" "}
            <b>{closedCount}</b> {t("closed_count")}
          </span>
        )}
      </div>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

      {loading && !tills ? (
        <p className="muted" style={{ marginTop: 16 }}>{t("loading")}</p>
      ) : tills && shown.length === 0 ? (
        <div style={{ ...CARD, padding: 16, marginTop: 14 }}>
          <div className="muted" style={{ fontSize: 13 }}>{t("none")}</div>
        </div>
      ) : tills ? (
        <div style={{ ...CARD, padding: 14, marginTop: 14, overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={TH}>{t("cashier")}</th>
              <th style={TH}>{t("point")}</th>
              <th style={{ ...TH, ...TR }}>{t("opening")}</th>
              <th style={{ ...TH, ...TR }}>{t("sales")}</th>
              <th style={{ ...TH, ...TR }}>{t("counted")}</th>
              <th style={{ ...TH, ...TR }}>{t("expected")}</th>
              <th style={{ ...TH, ...TR }}>{t("variance")}</th>
              <th style={TH}>{t("status")}</th>
            </tr></thead>
            <tbody>
              {shown.map((x) => {
                const v = x.cash_variance;
                const off = v != null && Math.abs(v) >= 0.01;
                return (
                  <tr key={x.id}>
                    <td style={{ padding: "5px 8px", fontWeight: 600 }}>{x.cashier_name || (x.cashier_staff_id != null ? `#${x.cashier_staff_id}` : "-")}</td>
                    <td style={{ padding: "5px 8px" }}>{x.point || "-"}</td>
                    <td style={TR}>{money(x.opening_float)}</td>
                    <td style={TR}>{money((x.totals || {}).gross_total)}</td>
                    <td style={TR}>{x.counted_cash == null ? "—" : money(x.counted_cash)}</td>
                    <td style={TR}>{x.expected_cash == null ? "—" : money(x.expected_cash)}</td>
                    <td style={{ ...TR, color: v == null ? "#8a949b" : off ? "#A32D2D" : "#0F6E56", fontWeight: off ? 700 : 500 }}>
                      {v == null ? "—" : money(v)}
                    </td>
                    <td style={{ padding: "5px 8px" }}>
                      <span style={{
                        fontSize: 11.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                        background: x.status === "open" ? "#FDF6E3" : "#E7F3EE",
                        color: x.status === "open" ? "#8A5A0B" : "#0F6E56",
                      }}>
                        {x.status === "open" ? t("open") : t("closed")}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
